import React from 'react';
import { StanButton } from '@/components';
import { SubscriptionSection } from './billing/SubscriptionSection';
import { UpdatePaymentMethodSection } from './billing/UpdatePaymentMethodSection';
import { InvoicesSection } from './billing/InvoicesSection';
import { StartTrialModal } from './billing/StartTrialModal';
import { CancellationFlowOrchestrator } from './cancellation';
import { ChangePlanModal } from '@/features/subscriptions/components/ChangePlanModal';
import { useBillingTab } from '../hooks/useBillingTab';
import IcLock from '@/assets/icons/ic-lock.svg';

export const BillingTab: React.FC = () => {
    const {
        // State
        loading,
        isCancellationFlowOpen,
        isChangePlanModalOpen,
        isStartTrialModalOpen,
        customerId,

        // Subscription info
        platformSubscription,
        platformSubscriptionId,
        currentSubscriptionStatus,
        currentBilledPlan,
        billingCycle,
        nextBillDate,
        pricingDetail,

        // Subscription type flags
        isAffiliateOnlySubscription,
        isMobileSubscription,

        // Trial info
        isNoExtraCostTrial,
        trialEndDate,
        trialExpirationMessage,

        // Discount info
        activeCouponName,
        isFreeForever,

        // Offer qualification
        shouldShowOffer,
        hasTakenFreeMonth,

        // Free trial state
        isInFreeTrial,

        // Actions
        handleCancelSubscription,
        handleCancellationFlowComplete,
        handleChangePlan,
        handleRenewSubscription,
        closeChangePlanModal,
        handleStartTrial,
        closeStartTrialModal,
    } = useBillingTab();

    return (
        <>
            <div className='space-y-8'>
                {/* Affiliate Upgrade Section - Shows blurred subscription with upgrade overlay */}
                {isAffiliateOnlySubscription ? (
                    <div className='relative w-full max-w-xl'>
                        {/* Blurred subscription content behind */}
                        <div className='pointer-events-none opacity-50 blur-xs select-none'>
                            <SubscriptionSection
                                currentSubscriptionStatus={currentSubscriptionStatus}
                                currentBilledPlan={currentBilledPlan}
                                pricingDetail={pricingDetail}
                                billingCycle={billingCycle}
                                nextBillDate={nextBillDate}
                                platformSubscription={platformSubscription}
                                loading={loading}
                                isAffiliateOnly
                            />
                        </div>

                        {/* Upgrade overlay */}
                        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/25'>
                            <StanButton
                                name='start-free-trial-button'
                                variant='outline'
                                size='sm'
                                onClick={handleStartTrial}
                                icon={IcLock}
                            >
                                Start My 14 Day Free Trial
                            </StanButton>
                        </div>
                    </div>
                ) : (
                    /* Regular subscription display for non-affiliates */
                    <div className='grid w-full max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2 xl:max-w-7xl'>
                        {/* Subscription Details */}
                        <SubscriptionSection
                            currentSubscriptionStatus={currentSubscriptionStatus}
                            currentBilledPlan={currentBilledPlan}
                            pricingDetail={pricingDetail}
                            billingCycle={billingCycle}
                            nextBillDate={nextBillDate}
                            platformSubscription={platformSubscription}
                            loading={loading}
                            isAffiliateOnly={false}
                            isNoExtraCostTrial={isNoExtraCostTrial}
                            trialEndDate={trialEndDate ?? undefined}
                            trialExpirationMessage={trialExpirationMessage}
                            activeCouponName={activeCouponName ?? undefined}
                            isFreeForever={isFreeForever}
                            isInFreeTrial={isInFreeTrial}
                            onCancelSubscription={handleCancelSubscription}
                            onChangePlan={handleChangePlan}
                            onRenewSubscription={handleRenewSubscription}
                        />

                        {/* Payment Details */}
                        {customerId && (
                            <UpdatePaymentMethodSection
                                isMobileSubscription={isMobileSubscription}
                                customerId={customerId}
                            />
                        )}
                    </div>
                )}

                {/* Invoices Table - Only for non-affiliates */}
                {!isAffiliateOnlySubscription && <InvoicesSection />}
            </div>

            {/* Cancellation Flow Orchestrator */}
            <CancellationFlowOrchestrator
                isOpen={isCancellationFlowOpen}
                platformSubscriptionId={platformSubscriptionId}
                hasTakenFreeMonth={hasTakenFreeMonth}
                shouldShowOffer={shouldShowOffer}
                onComplete={handleCancellationFlowComplete}
            />

            {/* Change Plan Modal */}
            <ChangePlanModal isOpen={isChangePlanModalOpen} onClose={closeChangePlanModal} />

            {/* Start Trial Modal - For affiliates to start their subscription */}
            <StartTrialModal isOpen={isStartTrialModalOpen} onClose={closeStartTrialModal} />
        </>
    );
};

export default BillingTab;
